import * as React from 'react';
import { useState } from 'react';
import { Dialog } from '@shared/ui-react/Dialog';
import { Button } from '@shared/ui-react/Button';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { BcfVersion, PageStateService } from '@shared/user-state/PageStateService';

/**
 * BCF version picker shown before the given saved views download as a single .bcfzip. 2.1 is
 * preselected: it is the version the mainstream CAD-plugin BCF managers (Solibri, Revit,
 * BIMcollab ZOOM) read and write; 3.0 is the current buildingSMART standard for viewers that
 * ask for it.
 */
export const BcfExportDialog = {
    open: (items: IPageState[]) => {
        ReactUtils.safeRender(<BcfExportDialogComponent items={items} />, document.getElementById('react-portal-root'));
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

const VERSIONS: { version: BcfVersion; label: string; hint: string }[] = [
    { version: '2.1', label: __('js_saved_views.export_bcf.version_21'), hint: __('js_saved_views.export_bcf.version_21_hint') },
    { version: '3.0', label: __('js_saved_views.export_bcf.version_30'), hint: __('js_saved_views.export_bcf.version_30_hint') },
];

const BcfExportDialogComponent: React.FC<{ items: IPageState[] }> = ({ items }) => {
    const [version, setVersion] = useState<BcfVersion>('2.1');

    const onExport = () => {
        // The export is a plain browser download (GET navigation), so there is nothing to await.
        PageStateService.exportBcf(
            items.map(item => item.uuid),
            version
        );
        BcfExportDialog.close();
    };

    return (
        <Dialog title={__('js_saved_views.export_bcf.title')} onClose={BcfExportDialog.close} closeOnEsc={true}>
            <div style={{ padding: '20px 25px', minWidth: 420 }}>
                <p style={{ marginTop: 0 }}>{__('js_saved_views.export_bcf.description')}</p>
                <div className='form-group'>
                    <label>{__('js_saved_views.export_bcf.version')}</label>
                    {VERSIONS.map(option => (
                        <label key={option.version} style={{ display: 'block', cursor: 'pointer', margin: '8px 0', fontWeight: 'normal' }}>
                            <input
                                type='radio'
                                name='bcf-export-version'
                                checked={version === option.version}
                                onChange={() => setVersion(option.version)}
                                style={{ marginRight: 8 }}
                            />
                            {option.label}
                            <div style={{ marginLeft: 22, color: '#888', fontSize: '0.9em' }}>{option.hint}</div>
                        </label>
                    ))}
                </div>
            </div>
            <footer style={{ padding: '15px 25px', background: 'white', borderTop: '1px solid #EFEFEF' }}>
                <Button className='ik-button-secondary--subtle' onClick={BcfExportDialog.close}>
                    {__('general.close')}
                </Button>
                <Button className='ik-button-primary right' onClick={onExport}>
                    {__('general.export')}
                    {items.length > 1 ? ` (${items.length})` : ''}
                </Button>
            </footer>
        </Dialog>
    );
};
